import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { TID } from "@/lib/testIds";

const NAV = [
  { key: "fahrzeuge", label: "Fahrzeuge", target: "#vehicles-section" },
  { key: "leistungen", label: "Leistungen", target: "#services-section" },
  { key: "ablauf", label: "Ablauf", target: "#manifesto-section" },
  { key: "konfigurator", label: "Konfigurator", target: "#konfigurator-section" },
  { key: "faq", label: "FAQ", target: "#faq-section" },
  { key: "kontakt", label: "Kontakt", target: "#contact-section" },
];

const scrollTo = (sel) => {
  const el = document.querySelector(sel);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Header({ portalUrl }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid={TID.header.root}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid={TID.header.logo}
          onClick={() => scrollTo("#hero-section")}
          className="flex items-center gap-3 shrink-0"
          aria-label="VPT Engineering – nach oben"
        >
          <img src="/vpt-logo.png" alt="VPT Engineering Logo" className="h-9 md:h-11 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.key}
              data-testid={TID.header.navLink(n.key)}
              onClick={() => scrollTo(n.target)}
              className="relative text-sm uppercase tracking-widest text-white/60 hover:text-white transition-colors duration-200 group"
            >
              {n.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-primary group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid={TID.header.loginCta}
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 hover:bg-white transition-colors duration-300"
          >
            Portal Login
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <button
            data-testid={TID.header.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2"
            aria-label="Menü öffnen"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={TID.header.mobileMenu}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <button
                  key={n.key}
                  onClick={() => { scrollTo(n.target); setOpen(false); }}
                  className="text-left font-display uppercase tracking-wider text-lg text-white/80 hover:text-primary transition-colors"
                >
                  {n.label}
                </button>
              ))}
              <a
                href={portalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-wider px-5 py-3"
              >
                Portal Login <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
